import { BigNumber, Contract, providers, Signer } from 'ethers';
import Contracts from '@tokenysolutions/t-rex';

import { contractFactory, OverrideTransactionsInterface } from '../utils/utils';

/**
 * Allow users to exchange one ERC20 token against another ERC20 token
 * @param {{signer: Signer; overrides: OverrideTransactionsInterface}} contract
 * @returns {Promise<Contract>}
 */
export async function deployDVD(contract: { signer: Signer; overrides: OverrideTransactionsInterface }): Promise<Contract> {
  const factory = contractFactory({
    abi: Contracts.contracts.DVDTransferManager.abi,
    bytecode: Contracts.contracts.DVDTransferManager.bytecode,
    signer: contract.signer,
  });
  const deploy = await factory.deploy(contract.overrides);
  return await deploy.deployed();
}

/* main functions */

/**
 * @param {{ trexToken: string; trexAmount: BigNumber; counterpart: string;erc20Token: string; erc20amount: BigNumber; dvd: Contract; overrides: OverrideTransactionsInterface}} tx
 * @returns {Promise<TransactionResponse>}
 */
export async function initiate(tx: {
  trexToken: string;
  trexAmount: BigNumber;
  counterpart: string;
  erc20Token: string;
  erc20amount: BigNumber;
  dvd: Contract;
  overrides: OverrideTransactionsInterface;
}): Promise<providers.TransactionResponse> {
  return await tx.dvd.initiateDVDTransfer(tx.trexToken, tx.trexAmount, tx.counterpart, tx.erc20Token, tx.erc20amount, tx.overrides);
}

/**
 * delete a pending DVD transfer that was previously initiated through the `initiateDVDTransfer` function from the pool
 * @param {{ transferID: string; dvd: Contract; overrides: OverrideTransactionsInterface}} tx
 *  transferID the DVD transfer identifier as calculated through the `calculateTransferID` function for the initiated DVD transfer to delete
 *  requires `transferID` to exist (DVD transfer has to be initiated)
 *  requires that `msg.sender` is the taker or the maker or the `DVDTransferManager` contract owner or the TREX agent in case a TREX token is involved in the transfer
 *  once the `cancelDVDTransfer` is executed the `transferID` is removed from the pending `transferID` pool
 *  emits a `DVDTransferCancelled` event
 * @returns {Promise<TransactionResponse>}
 */
export async function cancel(tx: { transferID: string; dvd: Contract; overrides: OverrideTransactionsInterface }): Promise<providers.TransactionResponse> {
  return await tx.dvd.cancelDVDTransfer(tx.transferID, tx.overrides);
}

/**
 * execute a DVD transfer that was previously initiated through the `initiateDVDTransfer` function
 * @param {{ transferID: string; dvd: Contract; overrides: OverrideTransactionsInterface}} tx
 *  transferID the DVD transfer identifier as calculated through the `calculateTransferID` function for the initiated DVD transfer to delete
 *  requires `transferID` to exist (DVD transfer has to be initiated)
 *  requires that taker (counterpart sending token2) has enough tokens in balance to process the DVD transfer
 *  requires that `DVDTransferManager` contract has enough allowance to process the `token2` leg of the DVD transfer
 *  requires that `msg.sender` is the taker OR the TREX agent in case a TREX token is involved in the transfer (in case of conditional transfer
 *  the agent can call the function when the transfer has been approved)
 *  if fees apply on one side or both sides of the transfer the fees will be sent, at transaction time, to the fees wallet previously set
 *  in case fees apply the counterparts will receive less than the amounts included in the DVD transfer as part of the transfer is redirected to the
 *  fee wallet at transfer execution time
 *  if one or both legs of the transfer are TREX, then all the relevant checks apply on the transaction (compliance + identity checks)
 *  and the transaction WILL FAIL if the TREX conditions of transfer are not respected, please refer to Token-transfer and Token-transferFrom to
 *  know more about TREX conditions for transfers
 *  once the DVD transfer is executed the `transferID` is removed from the pending `transferID` pool
 *  emits a `DVDTransferExecuted` event
 * @returns {Promise<TransactionResponse>}
 */
export async function take(tx: { transferID: string; dvd: Contract; overrides: OverrideTransactionsInterface }): Promise<providers.TransactionResponse> {
  return await tx.dvd.takeDVDTransfer(tx.transferID, tx.overrides);
}

/**
 * modify the fees applied to a parity of tokens (tokens can be TREX or ERC20)
 * @param {{ token1: string; token2: string; fee1: number; fee2: number; feeBase: number; fee1Wallet:string; fee2Wallet:string; dvd: Contract; overrides: OverrideTransactionsInterface}} tx
 *  transferID the DVD transfer identifier as calculated through the `calculateTransferID` function for the initiated DVD transfer to delete
 *  @param token1 the address of the base token for the parity `token1`/`token2`
 *  @param token2 the address of the counterpart token for the parity `token1`/`token2`
 *  @param fee1 the fee to apply on `token1` leg of the DVD transfer per 10^`feeBase`
 *  @param fee2 the fee to apply on `token2` leg of the DVD transfer per 10^`feeBase`
 *  @param feeBase the precision of the fee setting, e.g. if `feeBase` == 2 then `fee1` and `fee2` are in % (fee/10^`feeBase`)
 *  @param fee1Wallet the wallet address receiving fees applied on `token1`
 *  @param fee2Wallet the wallet address receiving fees applied on `token2`
 *  `token1` and `token2` need to be ERC20 or TREX tokens addresses, otherwise the transaction will fail
 *  `msg.sender` has to be owner of the DVD contract or the owner of the TREX token involved in the parity (if any)
 *  requires fees to be lower than 100%
 *  requires `feeBase` to be higher or equal to 2 (precision 10^2)
 *  requires `feeBase` to be lower or equal to 5 (precision 10^5) to avoid overflows
 *  requires `fee1Wallet` & `fee2Wallet` to be non empty addresses if `fee1` & `fee2` are respectively set
 *  note that if fees are not set for a parity the default fee is basically 0%
 *  emits a `FeeModified` event
 * @returns {Promise<TransactionResponse>}
 */
export async function modifyFee(tx: {
  token1: string;
  token2: string;
  fee1: number;
  fee2: number;
  feeBase: number;
  fee1Wallet: string;
  fee2Wallet: string;
  dvd: Contract;
  overrides: OverrideTransactionsInterface;
}): Promise<providers.TransactionResponse> {
  return await tx.dvd.modifyFee(tx.token1, tx.token2, tx.fee1, tx.fee2, tx.feeBase, tx.fee1Wallet, tx.fee2Wallet, tx.overrides);
}

/* helpers functions */

/**
 *  calculates the parity byte signature
 *  @param {{ token1: string; token2: string; dvd: Contract; overrides: OverrideTransactionsInterface}} tx
 *  @param token1 the address of the base token
 *  @param token2 the address of the counterpart token
 *  return the byte signature of the parity
 * @returns {Promise<TransactionResponse>}
 */
export async function calculateParity(tx: {
  token1: string;
  token2: string;
  dvd: Contract;
  overrides: OverrideTransactionsInterface;
}): Promise<providers.TransactionResponse> {
  return await tx.dvd.calculateParity(tx.token1, tx.token2, tx.overrides);
}

/**
 *  @dev calculates the fees to apply to a specific transfer depending on the fees applied to the parity used in the transfer
 *  @param {{ transferID: string; dvd: Contract; overrides: OverrideTransactionsInterface}} tx
 *  @param transferID the DVD transfer identifier as calculated through the `calculateTransferID` function for the transfer to calculate fees on
 *  requires `transferID` to exist (DVD transfer has to be initiated)
 *  returns the fees to apply on each leg of the transfer in the form of a `TxFees` struct
 * @returns {Promise<TransactionResponse>}
 */
export async function calculateFee(tx: {
  transferID: string;
  dvd: Contract;
  overrides: OverrideTransactionsInterface;
}): Promise<providers.TransactionResponse> {
  return await tx.dvd.calculateFee(tx.transferID, tx.overrides);
}

/* boolean functions */

/**
 *  @dev check if `token` corresponds to a functional TREX token (with identity registry initiated)
 *  @param {{ token: string;  dvd: Contract; overrides: OverrideTransactionsInterface}} tx
 *  @param token the address token to check
 *  the function will try to call `identityRegistry()` on the address, which is a getter specific to TREX tokens
 *  if the call pass and returns an address it means that the token is a TREX, otherwise it's not a TREX
 *  return `true` if the token is a TREX, `false` otherwise
 * @returns {Promise<TransactionResponse>}
 */
export async function isTREX(tx: { token: string; dvd: Contract; overrides: OverrideTransactionsInterface }): Promise<providers.TransactionResponse> {
  return await tx.dvd.isTREX(tx.token, tx.overrides);
}

/**
 * @dev check if `user` is a TREX agent of `token`
 *  @param {{ token: string; user: string; dvd: Contract; overrides: OverrideTransactionsInterface}} tx
 *  @param token the address token to check
 *  @param user the wallet address
 *  if `token` is a TREX token this function will check if `user` is registered as an agent on it
 *  return `true` if `user` is agent of `token`, return `false` otherwise
 * @returns {Promise<TransactionResponse>}
 */
export async function isTREXAgent(tx: {
  token: string;
  user: string;
  dvd: Contract;
  overrides: OverrideTransactionsInterface;
}): Promise<providers.TransactionResponse> {
  return await tx.dvd.isTREXAgent(tx.token, tx.user, tx.overrides);
}

/**
 *  @dev check if `user` is a TREX owner of `token`
 *  @param {{ token: string; user: string; dvd: Contract; overrides: OverrideTransactionsInterface}} tx
 *  @param token the address token to check
 *  @param user the wallet address
 *  if `token` is a TREX token this function will check if `user` is registered as an owner on it
 *  return `true` if `user` is owner of `token`, return `false` otherwise
 * @returns {Promise<TransactionResponse>}
 */
export async function isTREXOwner(tx: {
  token: string;
  user: string;
  dvd: Contract;
  overrides: OverrideTransactionsInterface;
}): Promise<providers.TransactionResponse> {
  return await tx.dvd.isTREXOwner(tx.token, tx.user, tx.overrides);
}

/**
 * Returns the Owner of the DVD contract.
 * @param {{dvd: Contract; overrides: OverrideTransactionsInterface}} tx
 * @returns {Promise<TransactionResponse>}
 */
export async function getOwnerOfDVD(tx: { dvd: Contract }): Promise<string> {
  return await tx.dvd.owner();
}

/**
 * Transfers the Ownership of the DVD to a new Owner.
 * @param {{newOwner: string; dvd: Contract; overrides: OverrideTransactionsInterface}} tx
 * @returns {Promise<TransactionResponse>}
 */
export async function transferDVDOwnership(tx: {
  newOwner: string;
  dvd: Contract;
  overrides: OverrideTransactionsInterface;
}): Promise<providers.TransactionResponse> {
  return await tx.dvd.transferOwnership(tx.newOwner, tx.overrides);
}
